// src/services/purchaseOrderReturnService.ts
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  runTransaction,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData,
  Query,
  limit,
  startAfter,
} from 'firebase/firestore';
import { db, storage } from '../../../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  PurchaseOrderReturn,
  PurchaseOrderReturnFormData,
  ReturnStatus,
  ReturnStats,
} from './PurchaseOrder';
import { PurchaseOrderService } from './purchaseOrderService';
import { WarehouseInventoryService } from '../product/WarehouseInventoryService';

const RETURNS_COLLECTION = 'purchaseOrderReturns';
const PO_COLLECTION = 'purchaseOrders';

export interface PaginatedReturnsResult {
  returns: PurchaseOrderReturn[];
  totalCount: number;
  // Kept for interface compatibility — always null now (seek-based pagination
  // doesn't need callers to pass cursors back in).
  lastVisible: QueryDocumentSnapshot<DocumentData> | null;
  firstVisible: QueryDocumentSnapshot<DocumentData> | null;
}

export class PurchaseOrderReturnService {
  static async generateReturnNumber(): Promise<string> {
    const year = new Date().getFullYear();
    const returnsRef = collection(db, RETURNS_COLLECTION);
    const q = query(returnsRef, orderBy('createdAt', 'desc'), limit(1));
    const snapshot = await getDocs(q);

    let nextNum = 1;
    if (!snapshot.empty) {
      const lastReturn = snapshot.docs[0].data();
      const lastNumber = lastReturn.returnNumber;
      const match = lastNumber.match(/RET-\d{4}-(\d{5})/);
      if (match && lastNumber.startsWith(`RET-${year}-`)) {
        nextNum = parseInt(match[1]) + 1;
      }
    }

    return `RET-${year}-${String(nextNum).padStart(5, '0')}`;
  }

  static async uploadReturnImages(returnId: string, images: File[]): Promise<string[]> {
    const uploadPromises = images.map(async (image, index) => {
      const imagePath = `returns/${returnId}/${Date.now()}_${index}_${image.name}`;
      const imageRef = ref(storage, imagePath);
      await uploadBytes(imageRef, image);
      return getDownloadURL(imageRef);
    });
    return Promise.all(uploadPromises);
  }

  static async createReturn(
    formData: PurchaseOrderReturnFormData,
    userId: string,
    userName: string
  ): Promise<string> {
    try {
      const po = await PurchaseOrderService.getPurchaseOrder(formData.purchaseOrderId);
      if (!po) throw new Error('Purchase order not found');

      if (po.status !== 'received') {
        throw new Error('Can only create returns for received purchase orders');
      }

      for (const returnItem of formData.items) {
        const poItem = po.items.find(
          item =>
            item.productId === returnItem.productId &&
            (item.variantId || '') === (returnItem.variantId || '')
        );

        if (!poItem) {
          throw new Error(`Product ${returnItem.productName} not found in purchase order`);
        }

        if (returnItem.quantity > poItem.receivedQuantity) {
          throw new Error(
            `Return quantity for ${returnItem.productName} exceeds received quantity (${poItem.receivedQuantity})`
          );
        }
      }

      const returnNumber = await this.generateReturnNumber();
      const totalAmount = formData.items.reduce((sum, item) => sum + item.totalPrice, 0);

      const returnData = {
        returnNumber,
        purchaseOrderId: formData.purchaseOrderId,
        purchaseOrderNumber: po.poNumber,
        supplierId: po.supplierId,
        supplierName: po.supplierName,
        supplierEmail: po.supplierEmail,
        supplierPhone: po.supplierPhone,
        status: ReturnStatus.PENDING,
        items: formData.items,
        totalAmount,
        refundAmount: 0,
        refundIssued: false,
        reason: formData.reason,
        notes: formData.notes || '',
        internalNotes: formData.internalNotes || '',
        images: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        createdByName: userName,
        inventoryAdjusted: false,
      };

      const docRef = await addDoc(collection(db, RETURNS_COLLECTION), returnData);

      if (formData.images && formData.images.length > 0) {
        const imageUrls = await this.uploadReturnImages(docRef.id, formData.images);
        await updateDoc(doc(db, RETURNS_COLLECTION, docRef.id), { images: imageUrls });
      }

      await updateDoc(doc(db, PO_COLLECTION, formData.purchaseOrderId), {
        hasReturns: true,
        returnIds: po.returnIds ? [...po.returnIds, docRef.id] : [docRef.id],
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating return:', error);
      throw error;
    }
  }

  static async updateReturnStatus(
    returnId: string,
    status: ReturnStatus,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      const updateData: any = { status, updatedAt: serverTimestamp() };

      if (status === ReturnStatus.APPROVED) {
        updateData.approvedBy = userId;
        updateData.approvedByName = userName;
        updateData.approvedAt = serverTimestamp();
      }

      const returnRef = doc(db, RETURNS_COLLECTION, returnId);
      await updateDoc(returnRef, updateData);
    } catch (error) {
      console.error('Error updating return status:', error);
      throw error;
    }
  }

  static async completeReturnWithInventoryAdjustment(
    returnId: string,
    refundAmount: number,
    userId: string,
    userName: string
  ): Promise<void> {
    try {
      await runTransaction(db, async (transaction) => {
        const returnRef = doc(db, RETURNS_COLLECTION, returnId);
        const returnDoc = await transaction.get(returnRef);

        if (!returnDoc.exists()) throw new Error('Return not found');

        const returnData = returnDoc.data();

        if (returnData.inventoryAdjusted) {
          throw new Error('Inventory has already been adjusted for this return');
        }

        if (returnData.status !== ReturnStatus.APPROVED) {
          throw new Error('Return must be approved before completing');
        }

        transaction.update(returnRef, {
          status: ReturnStatus.COMPLETED,
          refundAmount,
          refundIssued: true,
          completedBy: userId,
          completedByName: userName,
          completedAt: serverTimestamp(),
          inventoryAdjusted: true,
          updatedAt: serverTimestamp(),
        });
      });

      const returnData = await this.getReturn(returnId);
      if (!returnData) return;

      for (const item of returnData.items) {
        try {
          if (!item.warehouseId) {
            console.error(`No warehouse for item ${item.productName}, skipping`);
            continue;
          }

          const isVariant = item.variantId && item.variantId.trim() !== '';
          const existingInventory = await WarehouseInventoryService.getProductInventory(
            item.warehouseId,
            item.productId,
            isVariant ? item.variantId : undefined
          );

          if (existingInventory) {
            const newQuantity = Math.max(0, existingInventory.quantity - item.quantity);
            await WarehouseInventoryService.updateProductQuantity(
              item.warehouseId,
              item.productId,
              newQuantity,
              isVariant ? item.variantId : undefined
            );
          } else {
            console.error(`Inventory not found for ${item.productName} in warehouse ${item.warehouseName}`);
          }
        } catch (err) {
          console.error(`Error adjusting inventory for ${item.productName}:`, err);
        }
      }
    } catch (error) {
      console.error('Error completing return:', error);
      throw error;
    }
  }

  /* ===================== GET RETURNS PAGINATED =====================
   *
   * FIX: Replaced cursor-based pagination (startAfter / endBefore /
   * limitToLast) with a two-step cursor-seek so any page can be fetched
   * in any order (page 1 → page 5 → page 2 all work correctly).
   *
   * HOW IT WORKS
   * ─────────────
   * Firestore's client SDK has no offset(). The classic startAfter cursor
   * only works sequentially. To jump to page N we:
   *
   *   Step 1 — fetch only the first (page-1)*pageSize doc IDs (lightweight
   *             seek). The last doc from this set becomes our startAfter cursor.
   *   Step 2 — fetch the real pageSize docs starting after that cursor.
   *
   * For page 1 the seek is skipped entirely (skip count = 0).
   * ========================================================= */
  static async getReturnsPaginated(
    pageSize: number = 10,
    page: number = 1,
    searchQuery?: string,
    statusFilter?: ReturnStatus
  ): Promise<PaginatedReturnsResult> {
    try {
      const returns: PurchaseOrderReturn[] = [];
      const collectionRef = collection(db, RETURNS_COLLECTION);

      // ── Build filter constraints (where clauses only, no ordering/paging) ──
      const filterConstraints: any[] = [];
      if (statusFilter) filterConstraints.push(where('status', '==', statusFilter));

      const orderConstraint = orderBy('createdAt', 'desc');

      // ── Total count (0 doc reads) ──────────────────────────────────────────
      const countSnapshot = await getCountFromServer(
        query(collectionRef, ...filterConstraints)
      );
      const totalCount = countSnapshot.data().count;

      // ── Step 1: cursor seek ────────────────────────────────────────────────
      let cursorDoc: QueryDocumentSnapshot<DocumentData> | null = null;
      const skipCount = (page - 1) * pageSize;

      if (skipCount > 0) {
        const seekSnapshot = await getDocs(
          query(collectionRef, ...filterConstraints, orderConstraint, limit(skipCount))
        );
        if (!seekSnapshot.empty) {
          cursorDoc = seekSnapshot.docs[seekSnapshot.docs.length - 1];
        }
      }

      // ── Step 2: fetch the real page ────────────────────────────────────────
      const pageQuery: Query<DocumentData> = cursorDoc
        ? query(collectionRef, ...filterConstraints, orderConstraint, startAfter(cursorDoc), limit(pageSize))
        : query(collectionRef, ...filterConstraints, orderConstraint, limit(pageSize));

      const snapshot = await getDocs(pageQuery);

      // ── Map + client-side search filter ───────────────────────────────────
      snapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const returnDoc: PurchaseOrderReturn = {
          id: docSnap.id,
          returnNumber: data.returnNumber,
          purchaseOrderId: data.purchaseOrderId,
          purchaseOrderNumber: data.purchaseOrderNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          supplierEmail: data.supplierEmail,
          supplierPhone: data.supplierPhone,
          status: data.status,
          items: data.items || [],
          totalAmount: data.totalAmount,
          refundAmount: data.refundAmount || 0,
          refundIssued: data.refundIssued || false,
          reason: data.reason || '',
          notes: data.notes || '',
          internalNotes: data.internalNotes || '',
          images: data.images || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          approvedBy: data.approvedBy,
          approvedByName: data.approvedByName,
          approvedAt: data.approvedAt?.toDate() || undefined,
          completedBy: data.completedBy,
          completedByName: data.completedByName,
          completedAt: data.completedAt?.toDate() || undefined,
          inventoryAdjusted: data.inventoryAdjusted || false,
        };

        if (searchQuery && searchQuery.trim() !== '') {
          const searchLower = searchQuery.toLowerCase();
          const matchesSearch =
            returnDoc.returnNumber.toLowerCase().includes(searchLower) ||
            returnDoc.purchaseOrderNumber.toLowerCase().includes(searchLower) ||
            returnDoc.supplierName.toLowerCase().includes(searchLower) ||
            (returnDoc.notes && returnDoc.notes.toLowerCase().includes(searchLower));
          if (matchesSearch) returns.push(returnDoc);
        } else {
          returns.push(returnDoc);
        }
      });

      return {
        returns,
        totalCount,
        lastVisible: null,   // no longer used by callers
        firstVisible: null,  // no longer used by callers
      };
    } catch (error) {
      console.error('Error fetching returns:', error);
      throw error;
    }
  }

  static async getReturn(returnId: string): Promise<PurchaseOrderReturn | null> {
    try {
      const docRef = doc(db, RETURNS_COLLECTION, returnId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          returnNumber: data.returnNumber,
          purchaseOrderId: data.purchaseOrderId,
          purchaseOrderNumber: data.purchaseOrderNumber,
          supplierId: data.supplierId,
          supplierName: data.supplierName,
          supplierEmail: data.supplierEmail,
          supplierPhone: data.supplierPhone,
          status: data.status,
          items: data.items || [],
          totalAmount: data.totalAmount,
          refundAmount: data.refundAmount || 0,
          refundIssued: data.refundIssued || false,
          reason: data.reason || '',
          notes: data.notes || '',
          internalNotes: data.internalNotes || '',
          images: data.images || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          createdByName: data.createdByName,
          approvedBy: data.approvedBy,
          approvedByName: data.approvedByName,
          approvedAt: data.approvedAt?.toDate() || undefined,
          completedBy: data.completedBy,
          completedByName: data.completedByName,
          completedAt: data.completedAt?.toDate() || undefined,
          inventoryAdjusted: data.inventoryAdjusted || false,
        } as PurchaseOrderReturn;
      }

      return null;
    } catch (error) {
      console.error('Error fetching return:', error);
      throw error;
    }
  }

  static async getReturnsForPurchaseOrder(poId: string): Promise<PurchaseOrderReturn[]> {
    try {
      const q = query(
        collection(db, RETURNS_COLLECTION),
        where('purchaseOrderId', '==', poId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          approvedAt: data.approvedAt?.toDate() || undefined,
          completedAt: data.completedAt?.toDate() || undefined,
          inventoryAdjusted: data.inventoryAdjusted || false,
        } as PurchaseOrderReturn;
      });
    } catch (error) {
      console.error('Error fetching returns for PO:', error);
      throw error;
    }
  }

  static async deleteReturn(returnId: string): Promise<void> {
    try {
      const returnDoc = await this.getReturn(returnId);
      if (returnDoc && returnDoc.inventoryAdjusted) {
        throw new Error('Cannot delete a return that has already adjusted inventory');
      }
      const returnRef = doc(db, RETURNS_COLLECTION, returnId);
      await deleteDoc(returnRef);
    } catch (error) {
      console.error('Error deleting return:', error);
      throw error;
    }
  }

  /* ===================== GET RETURN STATS =====================
   * Uses getCountFromServer() for count fields (no document reads).
   * Financial aggregates (totalValue, totalRefunded) require document
   * data — fetched once in parallel with the count queries.
   * =========================================================== */
  static async getReturnStats(): Promise<ReturnStats> {
    try {
      const col = collection(db, RETURNS_COLLECTION);

      const [
        totalSnap,
        pendingSnap,
        approvedSnap,
        completedSnap,
        rejectedSnap,
        cancelledSnap,
        docsSnap,
      ] = await Promise.all([
        getCountFromServer(query(col)),
        getCountFromServer(query(col, where('status', '==', ReturnStatus.PENDING))),
        getCountFromServer(query(col, where('status', '==', ReturnStatus.APPROVED))),
        getCountFromServer(query(col, where('status', '==', ReturnStatus.COMPLETED))),
        getCountFromServer(query(col, where('status', '==', ReturnStatus.REJECTED))),
        getCountFromServer(query(col, where('status', '==', ReturnStatus.CANCELLED))),
        getDocs(query(col)),
      ]);

      let totalValue = 0;
      let totalRefunded = 0;
      docsSnap.forEach(d => {
        totalValue += d.data().totalAmount || 0;
        totalRefunded += d.data().refundAmount || 0;
      });

      return {
        total: totalSnap.data().count,
        pending: pendingSnap.data().count,
        approved: approvedSnap.data().count,
        completed: completedSnap.data().count,
        rejected: rejectedSnap.data().count,
        cancelled: cancelledSnap.data().count,
        totalValue,
        totalRefunded,
      };
    } catch (error) {
      console.error('Error getting return stats:', error);
      throw error;
    }
  }
}